/**
 * Sidebar layout component with left navigation menu.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import { getGravatarUrl } from '../utils/gravatar';
import { SunIcon, MoonIcon, LoginIcon, OAuthIcon, HomeIcon, AdminIcon, WrenchIcon, OpenStackIcon, KubernetesIcon } from './Icons';
import { useWebSocket } from '../contexts/WebSocketContext';
import { listClouds, listProjects, OpenStackCloud, OpenStackProject } from '../api/openstack';
import { SearchBar } from './SearchBar';
import { logError } from '../utils/errorLogging';
import './SidebarLayout.css';

interface SidebarLayoutProps {
  children: React.ReactNode;
}

export const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { theme, setTheme, effectiveTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isUserPage = location.pathname === '/profile' || location.pathname === '/preferences';
  const [userExpanded, setUserExpanded] = useState(isUserPage);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clouds, setClouds] = useState<OpenStackCloud[]>([]);
  const [projects, setProjects] = useState<OpenStackProject[]>([]);

  const appName = settings?.app_name || 'dasher';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isAdmin = user?.is_admin || false;
  const [adminExpanded, setAdminExpanded] = useState(isAdminPage);
  const isProjectsPage = location.pathname === '/projects';
  const isProjectDetailPage = location.pathname.startsWith('/projects/') && location.pathname.split('/').length === 3;
  const [projectsExpanded, setProjectsExpanded] = useState(isProjectsPage || isProjectDetailPage);
  
  // Admin menu is active only if we're on /admin (not on subpages)
  const isAdminActive = location.pathname === '/admin';
  // Submenu items are active if we're on their specific pages
  const isUsersActive = location.pathname === '/admin/users';
  const isSettingsActive = location.pathname === '/admin/settings';
  const isOpenStackActive = location.pathname === '/admin/openstack';
  const isKubernetesActive = location.pathname === '/admin/kubernetes';
  const isLocalLoginActive = location.pathname === '/admin/local-login';
  const isOAuthLoginActive = location.pathname === '/admin/oauth-login';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePreferences = () => {
    navigate('/preferences');
    setMobileMenuOpen(false);
    // Don't collapse - keep menu open
  };

  const handleAdminClick = () => {
    navigate('/admin');
    setAdminExpanded(true);
    setMobileMenuOpen(false);
  };

  const handleUsersClick = () => {
    navigate('/admin/users');
    setMobileMenuOpen(false);
  };

  const handleSettingsClick = () => {
    navigate('/admin/settings');
    setMobileMenuOpen(false);
  };

  const handleOpenStackClick = () => {
    navigate('/admin/openstack');
    setMobileMenuOpen(false);
  };

  const handleKubernetesClick = () => {
    navigate('/admin/kubernetes');
    setMobileMenuOpen(false);
  };

  const handleLocalLoginClick = () => {
    navigate('/admin/local-login');
    setMobileMenuOpen(false);
  };

  const handleOAuthLoginClick = () => {
    navigate('/admin/oauth-login');
    setMobileMenuOpen(false);
  };

  const handleUserClick = () => {
    navigate('/profile');
    setUserExpanded(true);
    setMobileMenuOpen(false);
  };

  const handleLogoutClick = async () => {
    setUserExpanded(false);
    await handleLogout();
  };

  const handleThemeToggle = () => {
    // If currently auto, switch to opposite of what auto is using
    // Otherwise, toggle between light and dark
    if (theme === 'auto') {
      setTheme(effectiveTheme === 'light' ? 'dark' : 'light');
    } else {
      setTheme(theme === 'light' ? 'dark' : 'light');
    }
  };

  // Get Gravatar URL
  const gravatarUrl = user?.email
    ? getGravatarUrl(user.email, 80, 'initials', user.name)
    : '';

  // Auto-expand admin menu when navigating to admin pages
  React.useEffect(() => {
    if (isAdminPage) {
      setAdminExpanded(true);
    }
  }, [isAdminPage]);

  // Auto-expand user menu when navigating to user pages
  React.useEffect(() => {
    if (isUserPage) {
      setUserExpanded(true);
    }
  }, [isUserPage]);

  const { on, off } = useWebSocket();

  // Load clouds and projects for sidebar submenu
  const loadCloudsAndProjects = useCallback(async () => {
    try {
      const [cloudsData, projectsData] = await Promise.all([
        listClouds(),
        listProjects()
      ]);
      setClouds(cloudsData);
      setProjects(projectsData);
    } catch (err) {
      logError('Failed to load clouds/projects for sidebar', err);
    }
  }, []);

  useEffect(() => {
    loadCloudsAndProjects();
  }, [loadCloudsAndProjects]);

  // Register WebSocket event handlers for project updates
  useEffect(() => {
    const handleProjectUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, cloud_id, project_data } = message.data || {};
      
      if (event_type === 'added') {
        // New cloud was added - reload to get updated list
        await loadCloudsAndProjects();
      } else if (event_type === 'updated') {
        // Project data was updated after refresh - update projects list
        if (project_data && Array.isArray(project_data)) {
          setProjects(prevProjects => {
            // Merge new projects with existing ones, replacing duplicates by id
            const projectMap = new Map(prevProjects.map(p => [p.id, p]));
            project_data.forEach((p: OpenStackProject) => projectMap.set(p.id, p as OpenStackProject));
            return Array.from(projectMap.values());
          });
        }
      } else if (event_type === 'deleted') {
        // Cloud was deleted - remove from state immediately
        setClouds(prevClouds => prevClouds.filter(c => c.id !== cloud_id));
        // Remove associated projects
        setProjects(prevProjects => prevProjects.filter(p => p.cloud_id !== cloud_id));
      }
    };

    // Register handler for project_update events
    const unsubscribe = on('project_update', handleProjectUpdate);

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [on, off, loadCloudsAndProjects]);

  // Auto-expand projects menu when on projects or project detail page
  useEffect(() => {
    if (isProjectsPage || isProjectDetailPage) {
      setProjectsExpanded(true);
    }
  }, [isProjectsPage, isProjectDetailPage]);

  // Close mobile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const sidebar = document.querySelector('.sidebar');
      const hamburger = document.querySelector('.mobile-hamburger');
      if (mobileMenuOpen && sidebar && hamburger && !sidebar.contains(target) && !hamburger.contains(target)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('touchstart', handleClickOutside, true);
      return () => {
        document.removeEventListener('click', handleClickOutside, true);
        document.removeEventListener('touchstart', handleClickOutside, true);
      };
    }
  }, [mobileMenuOpen]);

  return (
    <div className="sidebar-layout">
      {/* Top menu bar */}
      <div className="top-menu-bar">
        <button
          className="mobile-hamburger"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <Link to="/projects" className="top-menu-app-name">
          {appName}
        </Link>
        <SearchBar />
        <button
          className="top-menu-theme-toggle"
          onClick={handleThemeToggle}
          aria-label={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} theme`}
          type="button"
          title={`Current: ${effectiveTheme} (${theme === 'auto' ? 'auto' : theme})`}
        >
          {effectiveTheme === 'light' ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>
      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />}
      <div className="sidebar-layout-content">
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <nav className="sidebar-nav">
          <div className="sidebar-menu-section">
            <div className="sidebar-menu-item-wrapper">
              <div className="sidebar-menu-item-container">
                <Link
                  to="/projects"
                  className={`sidebar-menu-item ${isProjectsPage ? 'active' : ''}`}
                  onClick={() => {
                    setProjectsExpanded(true);
                    setMobileMenuOpen(false);
                  }}
                >
                  <span className="sidebar-menu-icon">
                    <HomeIcon />
                  </span>
                  <span>Projects</span>
                </Link>
                <button
                  className={`sidebar-menu-chevron ${projectsExpanded ? 'expanded' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setProjectsExpanded(!projectsExpanded);
                  }}
                  aria-label={projectsExpanded ? 'Collapse Projects menu' : 'Expand Projects menu'}
                  type="button"
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>
              {projectsExpanded && (
                <div className="sidebar-submenu">
                  {clouds
                    .sort((a, b) => a.cloud_name.localeCompare(b.cloud_name))
                    .map((cloud) => {
                      const project = projects.find((p) => p.cloud_id === cloud.id);
                      const isActive = isProjectDetailPage && project && location.pathname === `/projects/${project.project_id}`;
                      const hasProject = !!project;
                      
                      if (hasProject) {
                        return (
                          <Link
                            key={cloud.id}
                            to={`/projects/${project.project_id}`}
                            className={`sidebar-submenu-item ${isActive ? 'active' : ''}`}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {cloud.cloud_name}
                          </Link>
                        );
                      } else {
                        // Cloud without project yet - show but don't make it clickable
                        return (
                          <div
                            key={cloud.id}
                            className="sidebar-submenu-item"
                            style={{ opacity: 0.6, cursor: 'default' }}
                            title="Project data not yet available"
                          >
                            {cloud.cloud_name}
                          </div>
                        );
                      }
                    })}
                </div>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="sidebar-menu-section">
              <div className="sidebar-menu-item-wrapper">
                <div className="sidebar-menu-item-container">
                  <Link
                    to="/admin"
                    className={`sidebar-menu-item ${isAdminActive ? 'active' : ''}`}
                    onClick={handleAdminClick}
                  >
                    <span className="sidebar-menu-icon">
                      <AdminIcon />
                    </span>
                    <span>Admin</span>
                  </Link>
                  <button
                    className={`sidebar-menu-chevron ${adminExpanded ? 'expanded' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAdminExpanded(!adminExpanded);
                    }}
                    aria-label={adminExpanded ? 'Collapse Admin menu' : 'Expand Admin menu'}
                    type="button"
                  >
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                </div>
                {adminExpanded && (
                  <div className="sidebar-submenu">
                    <Link
                      to="/admin/users"
                      className={`sidebar-submenu-item ${isUsersActive ? 'active' : ''}`}
                      onClick={handleUsersClick}
                    >
                      <span className="sidebar-menu-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      </span>
                      <span>Users</span>
                    </Link>
                    <Link
                      to="/admin/settings"
                      className={`sidebar-submenu-item ${isSettingsActive ? 'active' : ''}`}
                      onClick={handleSettingsClick}
                    >
                      <span className="sidebar-menu-icon">
                        <WrenchIcon />
                      </span>
                      <span>Settings</span>
                    </Link>
                    <Link
                      to="/admin/openstack"
                      className={`sidebar-submenu-item ${isOpenStackActive ? 'active' : ''}`}
                      onClick={handleOpenStackClick}
                    >
                      <span className="sidebar-menu-icon">
                        <OpenStackIcon />
                      </span>
                      <span>OpenStack</span>
                    </Link>
                    <Link
                      to="/admin/kubernetes"
                      className={`sidebar-submenu-item ${isKubernetesActive ? 'active' : ''}`}
                      onClick={handleKubernetesClick}
                    >
                      <span className="sidebar-menu-icon">
                        <KubernetesIcon />
                      </span>
                      <span>Kubernetes</span>
                    </Link>
                    <Link
                      to="/admin/local-login"
                      className={`sidebar-submenu-item ${isLocalLoginActive ? 'active' : ''}`}
                      onClick={handleLocalLoginClick}
                    >
                      <span className="sidebar-menu-icon">
                        <LoginIcon />
                      </span>
                      <span>Local Login</span>
                    </Link>
                    <Link
                      to="/admin/oauth-login"
                      className={`sidebar-submenu-item ${isOAuthLoginActive ? 'active' : ''}`}
                      onClick={handleOAuthLoginClick}
                    >
                      <span className="sidebar-menu-icon">
                        <OAuthIcon />
                      </span>
                      <span>OAuth Login</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user-section">
            <div className="sidebar-menu-item-wrapper">
              <div className="sidebar-menu-item-container">
                <button
                  className={`sidebar-user-button ${location.pathname === '/profile' ? 'active' : ''}`}
                  onClick={handleUserClick}
                  aria-expanded={userExpanded}
                >
                  <div className="sidebar-user-avatar">
                    {gravatarUrl ? (
                      <img
                        src={gravatarUrl}
                        alt={user?.name || user?.email || 'User avatar'}
                        className="sidebar-user-avatar-img"
                      />
                    ) : (
                      <span className="sidebar-user-avatar-initials">
                        {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="sidebar-user-info">
                    <span className="sidebar-user-name">{user?.name || user?.email || 'User'}</span>
                  </div>
                </button>
                <button
                  className={`sidebar-menu-chevron ${userExpanded ? 'expanded' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setUserExpanded(!userExpanded);
                  }}
                  aria-label={userExpanded ? 'Collapse User menu' : 'Expand User menu'}
                  type="button"
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>
              {userExpanded && (
                <div className="sidebar-user-submenu">
                <button
                  className={`sidebar-submenu-item ${location.pathname === '/preferences' ? 'active' : ''}`}
                  onClick={handlePreferences}
                >
                  <span className="sidebar-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"/>
                    </svg>
                  </span>
                  <span>Preferences</span>
                </button>
                <button
                  className="sidebar-submenu-item"
                  onClick={handleLogoutClick}
                >
                  <span className="sidebar-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                  </span>
                  <span>Sign Out</span>
                </button>
              </div>
            )}
            </div>
          </div>
        </div>
      </aside>

      <main className="sidebar-content">
        {children}
      </main>
      </div>
    </div>
  );
};

